import { Label } from '@/components/shadcn/ui/label';
import {
  ToggleGroup,
  ToggleGroupItem,
} from '@/components/shadcn/ui/toggle-group';

import { useFieldContext } from './form';
import { getFieldError } from './get-field-error';

export interface ToggleGroupOption {
  value: string;
  label: string;
  disabled?: boolean;
}

interface FormToggleGroupProps extends Omit<
  React.ComponentProps<typeof ToggleGroup>,
  'value' | 'onValueChange' | 'type' | 'defaultValue'
> {
  label?: string;
  options: ToggleGroupOption[];
  showErrorMessage?: boolean;
}

export function FormToggleGroup({
  label,
  options,
  showErrorMessage = true,
  ...props
}: FormToggleGroupProps) {
  const field = useFieldContext<string>();

  return (
    <div className="flex flex-col gap-2">
      {label && (
        <Label htmlFor={field.name} className="text-sm font-medium">
          {label}
        </Label>
      )}
      <ToggleGroup
        type="single"
        value={field.state.value ?? ''}
        variant="outline"
        onValueChange={(value) => {
          if (value) {
            field.handleChange(value);
          }
        }}
        {...props}
      >
        {options.map((option) => (
          <ToggleGroupItem
            key={option.value}
            value={option.value}
            disabled={option.disabled}
          >
            {option.label}
          </ToggleGroupItem>
        ))}
      </ToggleGroup>
      {showErrorMessage && field.state.meta.errors.length > 0 && (
        <span className="text-destructive text-sm">
          {getFieldError(field.state.meta.errors[0])}
        </span>
      )}
    </div>
  );
}

interface FormToggleGroupMultipleProps extends Omit<
  React.ComponentProps<typeof ToggleGroup>,
  'value' | 'onValueChange' | 'type' | 'defaultValue'
> {
  label?: string;
  options: ToggleGroupOption[];
  showErrorMessage?: boolean;
}

export function FormToggleGroupMultiple({
  label,
  options,
  showErrorMessage = true,
  ...props
}: FormToggleGroupMultipleProps) {
  const field = useFieldContext<string[]>();

  return (
    <div className="flex flex-col gap-2">
      {label && (
        <Label htmlFor={field.name} className="text-sm font-medium">
          {label}
        </Label>
      )}
      <ToggleGroup
        type="multiple"
        value={field.state.value ?? []}
        onValueChange={(value) => field.handleChange(value)}
        {...props}
      >
        {options.map((option) => (
          <ToggleGroupItem
            key={option.value}
            value={option.value}
            disabled={option.disabled}
          >
            {option.label}
          </ToggleGroupItem>
        ))}
      </ToggleGroup>
      {showErrorMessage && field.state.meta.errors.length > 0 && (
        <span className="text-destructive text-sm">
          {getFieldError(field.state.meta.errors[0])}
        </span>
      )}
    </div>
  );
}
